/**
 * InterhubArbitrage — "¿me conviene haul-ear a otro hub?".
 *
 *  - Hauling cost input (default 500 ISK/m³ — public courier).
 *  - 5 hubs + local buy order price, computa net Δ después de haul.
 *  - Verdict per item: haul_to_hub / sell_local / indifferent / sin data.
 *  - Sort por net Δ (mejores arbitrajes arriba).
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

type Verdict = 'haul_to_hub' | 'sell_local' | 'indifferent' | 'insufficient_data'

type Row = {
  type_id: number
  name: string
  quantity: number
  volume_per_unit: number | null
  is_ore: boolean
  local: {
    best_buy: { price: number; volume_remain: number } | null
    buy_count: number
    region_id: number | null
  }
  hubs: Array<{
    hub_short: string
    region_id: number
    best_buy: { price: number; volume_remain: number } | null
    buy_order_count: number
  }>
  best_hub: { short: string; price: number; volume_remain: number } | null
  best_hub_vs_local: { delta_isk_per_unit: number; delta_pct: number } | null
  hauling_cost_per_unit: number | null
  net_delta_isk_per_unit: number | null
  net_breakeven_haul_isk_per_m3: number | null
  verdict: Verdict
  reason: string
}

export function InterhubArbitrage() {
  const [haulCost, setHaulCost] = useState(500)
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['interhub-arbitrage', forceRefetchKey, haulCost],
    queryFn: () => api.interhubArbitrage(haulCost, force),
  })

  const items: Row[] = data?.items ?? []
  const s = data?.summary
  const hubs = data?.settings_used.hubs ?? []
  const loc = data?.location
  const inSpace = loc?.kind === 'in_space'

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Inter-hub arbitrage</h1>
          <p className="text-eve-muted text-sm">
            "Si vendo este item en Hub X vs acá, ¿cuánto gano de más después del
            hauling?" — top 8 items del hangar dockeado.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Settings */}
      <div className="card flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-eve-muted">
            Hauling cost (ISK/m³)
          </label>
          <input
            type="number"
            min={0}
            step={50}
            value={haulCost}
            onChange={(e) => setHaulCost(Math.max(0, Number(e.target.value) || 0))}
            className="bg-eve-panel border border-eve-border rounded-md px-2 py-1 text-sm w-24 font-mono text-eve-text focus:border-eve-accent focus:outline-none"
          />
          <div className="flex gap-1">
            {[
              { label: 'Freighter', value: 0 },
              { label: 'Courier', value: 500 },
              { label: 'JF', value: 1500 },
            ].map((p) => (
              <button
                key={p.label}
                onClick={() => setHaulCost(p.value)}
                className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
                  haulCost === p.value
                    ? 'bg-eve-accent text-eve-bg border-eve-accent font-semibold'
                    : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary tiles */}
      {s && !inSpace && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryTile
            label="Net gain total"
            value={`${s.net_gain_total >= 0 ? '+' : ''}${formatISKShort(s.net_gain_total)} ISK`}
            sub="suma por todos los items"
            positive={s.net_gain_total > 0}
            negative={s.net_gain_total < 0}
          />
          <SummaryTile
            label="Haul to hub"
            value={String(s.total_haul_to_hub)}
            sub="items donde conviene haul-ear"
            accent={s.total_haul_to_hub > 0}
          />
          <SummaryTile
            label="Sell local"
            value={String(s.total_sell_local)}
            sub="items donde local gana"
          />
          <SummaryTile
            label="Indifferent / sin data"
            value={`${s.total_indifferent}/${s.total_insufficient_data}`}
            sub="marginales · sin precio"
          />
        </div>
      )}

      {/* Items */}
      <div className="card">
        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : inSpace ? (
          <div className="text-eve-muted py-8 text-center italic">
            Estás en space — volve a dockear para analizar el hangar.
          </div>
        ) : items.length === 0 ? (
          <div className="text-eve-muted py-8 text-center">
            No tenés items en el hangar dockeado.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <RowDisplay key={it.type_id} item={it} hubs={hubs} haulCost={haulCost} />
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-eve-muted">
        Buy orders no siempre son lo que cobrás: si tu qty excede el
        volume_remain, "llenás" la buy y el resto va a sell orders. Mostramos
        volume_remain para que sepas. No se contemplan broker fees / NPC taxes.
        KV cachea buy orders 5min.
      </p>
    </div>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  accent,
  positive,
  negative,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
  positive?: boolean
  negative?: boolean
}) {
  const cls = positive
    ? 'text-eve-success'
    : negative
      ? 'text-eve-danger'
      : accent
        ? 'text-eve-accent'
        : 'text-eve-text'
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${cls}`}>{value}</div>
      {sub && <div className="text-xs text-eve-muted mt-1 italic">{sub}</div>}
    </div>
  )
}

function RowDisplay({
  item,
  hubs,
  haulCost,
}: {
  item: Row
  hubs: Array<{ short: string; name: string; region_id: number }>
  haulCost: number
}) {
  const verdict = item.verdict
  const vCls =
    verdict === 'haul_to_hub'
      ? 'bg-eve-success/10 border-eve-success text-eve-success'
      : verdict === 'sell_local'
        ? 'bg-eve-muted/10 border-eve-border text-eve-muted'
        : 'bg-eve-warn/10 border-eve-warn text-eve-warn'

  const vLabel =
    verdict === 'haul_to_hub'
      ? 'Haul'
      : verdict === 'sell_local'
        ? 'Sell local'
        : verdict === 'indifferent'
          ? 'Indifferent'
          : 'Sin data'

  return (
    <details
      open={verdict === 'haul_to_hub'}
      className="border border-eve-border rounded-md bg-eve-bg/40"
    >
      <summary className="cursor-pointer p-3 hover:bg-eve-panel/50 transition-colors">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium">{item.name}</span>
              <span className="text-xs text-eve-muted font-mono">
                × {item.quantity.toLocaleString('en-US')}
              </span>
              {item.is_ore && (
                <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-eve-accent/10 border border-eve-accent/30 text-eve-accent">
                  ore
                </span>
              )}
            </div>
            <div className="text-xs text-eve-muted mt-1">{item.reason}</div>
          </div>

          <div className="text-right">
            {item.net_delta_isk_per_unit !== null ? (
              <>
                <div className="stat-label">Net Δ / u</div>
                <div
                  className={`font-mono text-base font-semibold ${
                    item.net_delta_isk_per_unit > 0
                      ? 'text-eve-success'
                      : item.net_delta_isk_per_unit < 0
                        ? 'text-eve-danger'
                        : 'text-eve-text'
                  }`}
                >
                  {item.net_delta_isk_per_unit >= 0 ? '+' : ''}
                  {formatISKShort(item.net_delta_isk_per_unit)}
                </div>
                <div className="text-[10px] text-eve-muted">
                  {item.net_delta_isk_per_unit >= 0 ? '+' : ''}
                  {formatISK(item.net_delta_isk_per_unit * item.quantity, 0)} total
                </div>
              </>
            ) : (
              <div className="text-eve-muted text-xs italic">—</div>
            )}
          </div>

          <span
            className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border whitespace-nowrap ${vCls}`}
          >
            {vLabel}
          </span>
        </div>
      </summary>

      <div className="border-t border-eve-border px-3 py-3 text-xs space-y-3">
        {/* Hub grid */}
        <div>
          <div className="stat-label mb-2">Best buy order por hub</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <HubCard
              label="Local"
              hubShort={
                item.local.region_id
                  ? hubs.find((h) => h.region_id === item.local.region_id)?.short ?? 'local'
                  : 'local'
              }
              bestBuy={item.local.best_buy}
              count={item.local.buy_count}
              isLocal
            />
            {hubs.map((h) => {
              const hp = item.hubs.find((x) => x.region_id === h.region_id)
              return (
                <HubCard
                  key={h.region_id}
                  label={h.name}
                  hubShort={h.short}
                  bestBuy={hp?.best_buy ?? null}
                  count={hp?.buy_order_count ?? 0}
                  highlight={hp?.best_buy?.price === item.best_hub?.price && item.best_hub?.short === h.short}
                />
              )
            })}
          </div>
        </div>

        {/* Math */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-eve-border/40">
          <Row label="Volume / unit" value={item.volume_per_unit !== null ? `${item.volume_per_unit.toFixed(2)} m³` : '—'} />
          <Row label="Haul cost / unit" value={item.hauling_cost_per_unit !== null ? formatISK(item.hauling_cost_per_unit, 2) : '—'} />
          <Row
            label="Δ bruto vs local"
            value={
              item.best_hub_vs_local
                ? `${item.best_hub_vs_local.delta_pct >= 0 ? '+' : ''}${item.best_hub_vs_local.delta_pct.toFixed(1)}%`
                : '—'
            }
          />
          <Row
            label="Breakeven haul"
            value={
              item.net_breakeven_haul_isk_per_m3 !== null
                ? `${formatISKShort(item.net_breakeven_haul_isk_per_m3)} ISK/m³`
                : '—'
            }
          />
        </div>

        <div className="text-[10px] text-eve-muted italic pt-1 border-t border-eve-border/40">
          Config: {haulCost} ISK/m³ × {item.volume_per_unit?.toFixed(2) ?? '?'} m³ ={' '}
          {item.hauling_cost_per_unit !== null ? formatISK(item.hauling_cost_per_unit, 2) : '—'} haul/u.
        </div>
      </div>
    </details>
  )
}

function HubCard({
  label,
  hubShort,
  bestBuy,
  count,
  highlight,
  isLocal,
}: {
  label: string
  hubShort: string
  bestBuy: { price: number; volume_remain: number } | null
  count: number
  highlight?: boolean
  isLocal?: boolean
}) {
  return (
    <div
      className={`border rounded p-2 ${
        highlight
          ? 'border-eve-accent bg-eve-accent/5'
          : isLocal
            ? 'border-eve-border bg-eve-bg/30'
            : 'border-eve-border/60'
      }`}
    >
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase font-mono text-eve-muted">
          {label}
        </div>
        {highlight && (
          <span className="text-[9px] uppercase font-mono text-eve-accent">
            best
          </span>
        )}
      </div>
      {bestBuy ? (
        <>
          <div className="font-mono text-sm font-semibold mt-0.5">
            {formatISK(bestBuy.price, 2)}
          </div>
          <div className="text-[10px] text-eve-muted mt-0.5">
            {count} orders · {bestBuy.volume_remain.toLocaleString('en-US')} qty top
          </div>
        </>
      ) : (
        <div className="text-xs text-eve-muted italic mt-0.5">Sin buy orders</div>
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-eve-muted">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}
